import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { SortDirection } from "../../tables/table";

const CountSchema = z.object({ count: z.number() });
const ValueSchema = z.object({ value: z.number() });

export const ReturnReasonSchema = z.object({
  reason: z.string(),
  count: z.number(),
  aiReturnReasons: z
    .array(
      z.object({
        summary: z.string().nullish(),
        returnId: z.string(),
        orderName: z.string(),
      }),
    )
    .nullish(),
});
export type ReturnReason = z.infer<typeof ReturnReasonSchema>;

export const ProductSchema = z
  .object({
    id: z.string(),
    vendor: z.string().nullish(),
    tags: z.array(z.string()).nullish(),
    item: z.object({ image: z.any().optional(), name: z.string() }),
    returned: CountSchema.describe(
      "The number of times a specific item was returned",
    ),
    sold: CountSchema.describe(
      "The number of times a particular item was purchased",
    ),
    returnRevenue: ValueSchema.describe(
      "The total value of items that were returned",
    ),
    returnRate: ValueSchema.describe(
      "Percentage of returned items from the total number of items purchased. Formula: Return rate = # of items returned / # of items purchased",
    ),
    exchangeRate: ValueSchema.describe(
      "The percentage of items exchanged from the total items purchased. Formula: Exchange rate = # of items exchanged / # of total items purchased",
    ),
    salesChannel: z
      .array(z.string())
      .nullish()
      .describe("The sales channels where the product was sold"),
    returnReasons: z
      .array(ReturnReasonSchema)
      .nullish()
      .describe(
        "Return reasons list the return reasons that are in your return flow for all items",
      ),
  })
  .describe(
    "Products analytics data. This data helps merchants identify which products are driving the most returns or exchanges, revealing potential issues with product quality, sizing, descriptions, or customer expectations. By analyzing return rates alongside quantity sold and return reasons, merchants can pinpoint problem items and make informed decisions—such as updating product listings, improving images, or discontinuing underperforming SKUs. Monitoring exchange rates also highlights whether customers are finding acceptable alternatives, which can inform inventory planning. Overall, these insights support better product curation, reduce operational costs, and improve customer satisfaction.",
  );
export type Product = z.infer<typeof ProductSchema>;

export enum ProductValue {
  Item = "item",
  Vendor = "vendor",
  Tags = "tags",
  Returned = "returned",
  Sold = "sold",
  ReturnRevenue = "returnRevenue",
  ReturnRate = "returnRate",
  ExchangeRate = "exchangeRate",
  SalesChannel = "salesChannel",
}

export enum ProductSortKey {
  Item = ProductValue.Item,
  Vendor = ProductValue.Vendor,
  Returned = ProductValue.Returned,
  Sold = ProductValue.Sold,
  ReturnRevenue = ProductValue.ReturnRevenue,
  ReturnRate = ProductValue.ReturnRate,
  ExchangeRate = ProductValue.ExchangeRate,
  SalesChannel = ProductValue.SalesChannel,
}

export const ProductSortSchema = z.object({
  key: createEnum(ProductSortKey),
  direction: createEnum(SortDirection),
});

export type ProductSort = z.infer<typeof ProductSortSchema>;

export const VariantSchema = z.object({
  id: z.string(),
  productId: z.string(),
  item: z.object({ image: z.any(), name: z.string(), variant: z.string() }),
  returned: CountSchema.describe(
    "The number of times a specific variant was returned",
  ),
  sold: CountSchema.describe(
    "The number of times a particular variant was purchased",
  ),
  returnRevenue: ValueSchema.describe(
    "The total value of the variant that was returned",
  ),
  returnRate: ValueSchema.describe(
    "Percentage of returned variants from the total number of variants purchased. Formula: Return rate = # of variants returned / # of variants purchased",
  ),
  exchangeRate: ValueSchema.describe(
    "The percentage of variants exchanged from the total variants purchased. Formula: Exchange rate = # of variants exchanged / # of total variants purchased",
  ),
  returnReasons: z.array(ReturnReasonSchema).optional(),
});
export type Variant = z.infer<typeof VariantSchema>;

export enum VariantValue {
  VariantTitle = "variantTitle",
  Returned = "returned",
  Sold = "sold",
  ReturnRevenue = "returnRevenue",
  ReturnRate = "returnRate",
  ExchangeRate = "exchangeRate",
}

export enum VariantSortKey {
  VariantTitle = VariantValue.VariantTitle,
  Returned = VariantValue.Returned,
  Sold = VariantValue.Sold,
  ReturnRevenue = VariantValue.ReturnRevenue,
  ReturnRate = VariantValue.ReturnRate,
  ExchangeRate = VariantValue.ExchangeRate,
}

export const VariantSortSchema = z.object({
  key: createEnum(VariantSortKey),
  direction: createEnum(SortDirection),
});
export type VariantSort = z.infer<typeof VariantSortSchema>;
